import { match, P } from 'ts-pattern';
import {
  CalendarYearSingleValueByMonthsSegment,
  FinancialYearSingleValueByYearsSegment,
} from '../../../api/types-graphql';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  FinancialYearSingleValueByYearsTimeSelection,
  SingleValueTimeSelection,
  TimeSelectionType,
} from '../../../types';
import { percentage } from '../../../utils';
import {
  format,
  getMomentDate,
  removeMonth,
  removeYear,
} from '../../../utils-date';
import { formatPercentageNumber } from '../../../utils-display';
import { getDeltaSign } from '../../utils';
import {
  KpiBenchmarkConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiQueryConfig,
} from '../dashboards/types';

export const getKpiBenchmarkTimeSelection = (
  mainTimeSelection: SingleValueTimeSelection,
  benchmarkPeriod: KpiFactboxBenchmarkPeriods
): SingleValueTimeSelection => {
  const newTimeSelection: SingleValueTimeSelection = match({
    mainTimeSelection,
    benchmarkPeriod,
  })
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        mainTimeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: P.select(),
        },
      },
      (input) => {
        // TODO: use better type than the any coming from graphql
        const { start, end } = input;
        const yearOnYearTimeSelectionInput: CalendarYearSingleValueByMonthsSegment =
          {
            start: format(removeYear(getMomentDate(start).valueOf(), 1)),
            end: format(removeYear(getMomentDate(end).valueOf(), 1)),
          };
        return {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: yearOnYearTimeSelectionInput,
        } as CalendarYearSingleValueByMonthsTimeSelection;
      }
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        mainTimeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: P.select(),
        },
      },
      (input) => {
        // TODO: use better type than the any coming from graphql
        const { start, end } = input;
        const priorPeriodTimeSelectionInput: CalendarYearSingleValueByMonthsSegment =
          {
            start: format(removeMonth(getMomentDate(start).valueOf(), 1)),
            end: format(removeMonth(getMomentDate(end).valueOf(), 1)),
          };
        return {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: priorPeriodTimeSelectionInput,
        } as CalendarYearSingleValueByMonthsTimeSelection;
      }
    )
    .with(
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        mainTimeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: P.select(),
        },
      },
      (input) => {
        const { start, end } = input;
        const priorPeriodTimeSelectionInput: FinancialYearSingleValueByYearsSegment =
          {
            start: start - 1,
            end: end - 1,
          };
        return {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: priorPeriodTimeSelectionInput,
        } as FinancialYearSingleValueByYearsTimeSelection;
      }
    )
    .otherwise(() => {
      throw new Error(
        `Not yet implemented - Benchmark Period: ${benchmarkPeriod} and main time selection ${mainTimeSelection.type}`
      );
    });
  return newTimeSelection;
};

export const getBenchmarkQueries = (
  mainQuery: KpiQueryConfig,
  benchmarkConfigs: KpiBenchmarkConfig[]
): KpiQueryConfig[] => {
  const { timeSelection } = mainQuery;
  const benchmarkQueries: KpiQueryConfig[] = benchmarkConfigs.map(
    (benchmarkConfig) => {
      const newTimeSelection = getKpiBenchmarkTimeSelection(
        timeSelection,
        benchmarkConfig.benchmarkPeriod
      );
      return {
        ...mainQuery,
        timeSelection: newTimeSelection,
      };
    }
  );
  return benchmarkQueries;
};

export const getFormattedKpiBenchmarkValue = (
  benchmarkType: KpiFactboxBenchmarkTypes,
  benchmarkQueryValue: number | null,
  mainQueryValue: number | null,
  benchmarkQueryValueFormatter: (value: number) => string,
  nullValue: string
) => {
  if (benchmarkQueryValue === null) return nullValue;
  return match(benchmarkType)
    .with(KpiFactboxBenchmarkTypes.VALUE, () => {
      return benchmarkQueryValueFormatter(benchmarkQueryValue);
    })
    .with(KpiFactboxBenchmarkTypes.DELTA, () => {
      if (mainQueryValue === null) return nullValue;
      const delta = mainQueryValue - benchmarkQueryValue;
      const sign = getDeltaSign(delta);
      return `${sign}${benchmarkQueryValueFormatter(delta)}`;
    })
    .with(KpiFactboxBenchmarkTypes.DELTA_PERCENT, () => {
      if (mainQueryValue === null) return nullValue;
      const deltaPercent = percentage(
        mainQueryValue - benchmarkQueryValue,
        benchmarkQueryValue
      );
      const sign = getDeltaSign(deltaPercent);
      return `${sign}${formatPercentageNumber(deltaPercent)}`;
    })
    .exhaustive();
};
